/**
 * Per `fan-scene-customization` spec: each fan's per-character overlay
 * layer of scene objects. The shape mirrors `SceneObjectSchema` from
 * character.ts with two differences:
 *   1. `aiDescription` is stripped — fan-placed objects are never folded
 *      into the LLM system prompt. The field is .omit()'d.
 *   2. `visibility` is narrowed to `'always'`. Any incoming
 *      'ai-controlled' value is coerced (the LLM doesn't see overlay
 *      objects, so 'ai-controlled' is meaningless here).
 *
 * Storage location: DDB `nx-fan-character-scenes-{env}`,
 * PK = `${fanId}#${characterId}`, attribute `items: FanSceneItem[]`,
 * cap 16 entries per fan per character.
 *
 * The composed scene rendered by every fan-facing viewport is
 *   [...operatorBase, ...fanOverlay]
 * Owner-only — anonymous + non-owner callers never see the overlay.
 */
import { z } from 'zod';
import { SceneObjectSchema } from './character';
/** One overlay entry — operator scene-object shape minus aiDescription, with visibility forced to 'always'. */
export const FanSceneItemSchema = SceneObjectSchema
    .omit({ aiDescription: true })
    .extend({
    visibility: z.literal('always').default('always'),
});
/** PUT /api/fan/characters/{id}/my-scene request body. */
export const FanScenePutBodySchema = z.object({
    items: z.array(FanSceneItemSchema).max(16),
});
/** Compose operator base + fan overlay. Order matters — base first
 *  so its ids appear earlier; overlay items are appended. Runtime
 *  load order matches array order. */
export function composeFanScene(operatorBase, fanOverlay) {
    const base = operatorBase ?? [];
    const overlay = fanOverlay ?? [];
    return [...base, ...overlay];
}
/** Coerce a putative item to the overlay shape — strip aiDescription
 *  and force visibility='always'. Used at ingest time by the PUT
 *  handler; the zod schema will accept the coerced object cleanly. */
export function coerceToFanSceneItem(raw) {
    const { aiDescription: _drop, ...rest } = raw;
    return { ...rest, visibility: 'always' };
}
